import { useState } from 'react';

interface ProductQuoteFormProps {
  productName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ProductQuoteForm({ productName, isOpen, onClose }: ProductQuoteFormProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d778nd2bj7ht1ofp4g5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg p-6 md:p-8 z-10 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 cursor-pointer">
          <i className="ri-close-line text-xl" />
        </button>

        {status === 'success' ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">Quote Request Sent!</h3>
            <p className="text-gray-500 text-sm mb-6">We&apos;ll get back to you within 2 business hours with pricing and availability.</p>
            <button onClick={onClose} className="px-6 py-2.5 bg-yellow-400 text-gray-900 font-bold rounded-md text-sm cursor-pointer whitespace-nowrap">Close</button>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <span className="inline-block bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-2">Quote Request</span>
              <h2 className="text-xl font-black text-gray-900">Get a Price for</h2>
              <p className="text-yellow-700 font-semibold text-sm mt-0.5">{productName}</p>
            </div>

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
              <input type="hidden" name="product" value={productName} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name *</label>
                  <input name="full_name" required type="text" placeholder="John Smith" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Company</label>
                  <input name="company" type="text" placeholder="ABC Ltd" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                  <input name="email" required type="email" placeholder="you@company.com" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Phone / WhatsApp</label>
                  <input name="phone" type="tel" placeholder="+1 555 000 0000" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Quantity (MT) *</label>
                  <input name="quantity" required type="text" placeholder="e.g. 50 MT" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Destination Port *</label>
                  <input name="destination" required type="text" placeholder="e.g. Lagos, Nigeria" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Specifications / Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  placeholder="Size, grade, thickness, delivery date, or any other requirements..."
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400 resize-none"
                />
              </div>
              {status === 'error' && <p className="text-red-500 text-xs">Something went wrong. Please try WhatsApp instead.</p>}
              <div className="flex gap-3 pt-1">
                <a
                  href="https://wa.me/8617671262710"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-700 py-2.5 rounded-md text-sm font-semibold hover:border-gray-900 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-green-500" /> WhatsApp
                </a>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="flex-1 bg-yellow-400 text-gray-900 py-2.5 rounded-md text-sm font-black hover:bg-yellow-500 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Quote Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
